/*
 * Section nav — a minimal vertical dot rail.
 *
 * A fixed column of dots on the right edge, one per section, that fades
 * in once the reader scrolls past the hero intro. A scroll-spy fills the
 * dot for the section currently in view; hovering a dot reveals its label.
 */

import type { SectionNavContent, SectionId } from "../i18n/types";

/** Section anchor ids in display order — structural, shared with the
 * section renderers' root ids. */
const NAV_IDS: SectionId[] = [
  "hero", "consortium", "platform", "works-for", "features",
  "security", "models", "resources", "faq", "contact",
];

export function renderSectionNav(t: SectionNavContent): HTMLElement {
  const root = document.createElement("nav");
  root.className = "sectionnav";
  root.setAttribute("aria-label", t.ariaLabel);
  root.innerHTML = NAV_IDS.map(
    (id) => `
    <a class="sectionnav__dot" href="#${id}" data-target="${id}" data-label="${t.labels[id]}" aria-label="${t.labels[id]}"></a>`,
  ).join("");
  return root;
}

export function initSectionNav(nav: HTMLElement): () => void {
  const dots = Array.from(
    nav.querySelectorAll<HTMLAnchorElement>(".sectionnav__dot"),
  );

  let activeId = "";

  const setActive = (id: string): void => {
    if (!id || id === activeId) return;
    activeId = id;
    for (const dot of dots) {
      const isActive = dot.dataset.target === id;
      dot.classList.toggle("is-active", isActive);
      if (isActive) dot.setAttribute("aria-current", "true");
      else dot.removeAttribute("aria-current");
    }
  };

  /* Scroll spy: the section covering the upper-middle band of the viewport
   * is the one the reader is in. Sections stack contiguously, so exactly
   * one section always covers the band; between updates the last winner
   * sticks (which also covers short trailing sections like the final CTA). */
  const spy = new IntersectionObserver(
    (entries) => {
      for (const entry of entries) {
        if (entry.isIntersecting) setActive(entry.target.id);
      }
    },
    { rootMargin: "-35% 0px -60% 0px" },
  );
  for (const item of NAV_IDS) {
    const target = document.getElementById(item);
    if (target) spy.observe(target);
  }

  /* Reveal the rail once the hero intro has scrolled off. */
  const onScroll = (): void => {
    nav.classList.toggle("is-visible", window.scrollY > 160);
  };
  window.addEventListener("scroll", onScroll, { passive: true });
  onScroll();

  /* Anchor jumps are smooth via CSS; update the spy state immediately so
   * the bar reacts on click instead of waiting for the scroll to land. */
  const onClick = (event: Event): void => {
    const dot = event.currentTarget as HTMLAnchorElement;
    setActive(dot.dataset.target ?? "");
  };
  for (const dot of dots) dot.addEventListener("click", onClick);

  return () => {
    spy.disconnect();
    window.removeEventListener("scroll", onScroll);
    for (const dot of dots) dot.removeEventListener("click", onClick);
  };
}
